import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.P
import org.jetbrains.compose.web.dom.Table
import org.jetbrains.compose.web.dom.Tbody
import org.jetbrains.compose.web.dom.Td
import org.jetbrains.compose.web.dom.Text
import org.jetbrains.compose.web.dom.Th
import org.jetbrains.compose.web.dom.Thead
import org.jetbrains.compose.web.dom.Tr

/**
 * Everyone's day in one editable table (SPECIFIKACE.md ch. 8 point 3) -- for filling in on behalf
 * of somebody who forgot. Saving sends only the cells that were actually edited, so two people
 * having the grid open at once do not overwrite each other's rows.
 *
 * The name column stays put while the exercise columns scroll sideways (`.col-sticky`).
 */
@Composable
fun GridScreen(token: String, actingParticipantId: String) {
    val state = rememberAsyncState()
    var participants by remember { mutableStateOf<List<Participant>>(emptyList()) }
    var exerciseTypes by remember { mutableStateOf<List<ExerciseType>>(emptyList()) }
    var ratingTiers by remember { mutableStateOf<List<RatingTier>>(emptyList()) }
    var illnesses by remember { mutableStateOf<List<Illness>>(emptyList()) }
    var selectedDate by remember { mutableStateOf(todayIso()) }
    var edits by remember { mutableStateOf(EntryEdits()) }
    var savedDate by remember { mutableStateOf<String?>(null) }
    var editRevision by remember { mutableStateOf(0) }

    suspend fun loadEntries(date: String) {
        val entries = Api.fetchEntries(token, date = date)
        // Ignore a response that lost the race to a newer date selection.
        if (date == selectedDate) {
            edits = EntryEdits.from(entries)
            participants = participants.sortedByDescending { edits.pointsOf(it.id, exerciseTypes) }
        }
    }

    LaunchedEffect(Unit) {
        state.run {
            participants = Api.fetchParticipants()
            exerciseTypes = Api.fetchExerciseTypes()
            ratingTiers = Api.fetchRatingTiers()
            illnesses = Api.fetchIllnesses(token)
            loadEntries(selectedDate)
        }
    }

    fun changeDate(date: String) {
        selectedDate = date
        savedDate = null
        state.launch { loadEntries(date) }
    }

    fun changeEdits(updated: EntryEdits) {
        edits = updated
        editRevision += 1
        savedDate = null
    }

    fun save() {
        val date = selectedDate
        val revision = editRevision
        val changes = edits.changes(exerciseTypes)
        state.launch {
            changes.save(token, date, actingParticipantId)
            if (selectedDate == date) edits = edits.committed(changes)
            if (editRevision == revision && selectedDate == date) savedDate = date
            participants = participants.sortedByDescending { edits.pointsOf(it.id, exerciseTypes) }
        }
    }

    Div {
        DateSelector(selectedDate = selectedDate, onChangeDate = ::changeDate)
        ErrorMessage(state.error)

        when {
            !state.loaded -> LoadingText()
            participants.isEmpty() -> P { Text("Zatím žádní účastníci -- přidej je v administraci.") }
            else -> {
                Div(attrs = { classes("table-scroll") }) {
                    Table {
                        Thead {
                            Tr {
                                Th(attrs = { classes("col-sticky") }) { Text("Jméno") }
                                exerciseTypes.forEach { Th { Text("${it.name} (${it.unitLabel})") } }
                                Th { Text("Body dnes") }
                                Th { Text("Hodnocení") }
                            }
                        }
                        Tbody {
                            participants.forEach { participant ->
                                val points = edits.pointsOf(participant.id, exerciseTypes)
                                Tr {
                                    Td(attrs = { classes("col-sticky") }) { Text(participant.name) }
                                    exerciseTypes.forEach { type ->
                                        val key = EntryKey(participant.id, type.id)
                                        Td {
                                            TextField(
                                                value = edits.textOf(key),
                                                onChange = { changeEdits(edits.withText(key, it)) },
                                                decimal = true,
                                                cssClass = "grid-input",
                                            )
                                        }
                                    }
                                    Td { Text(formatNumber(points)) }
                                    Td {
                                        if (illnesses.any {
                                                it.participantId == participant.id && it.includes(selectedDate)
                                            }
                                        ) {
                                            TierBadge("Nemocný")
                                        } else {
                                            ratingTiers.tierFor(points)?.let { TierBadge(it.label) }
                                        }
                                    }
                                }
                            }
                        }
                    }
                }
                SaveRow("Uložit vše", state.busy, savedDate, selectedDate, ::save)
            }
        }
    }
}
